// CloudWatch Logs deep-link builder (EMS-8614 design v2).
//
// Builds a link into the PPS Lambda log group's *filtered* log-events view,
// pre-filtered to ONE payment-bot transaction within a time window around when
// it ran. Clicking it from a failed-attempt row drops the engineer straight
// onto that transaction's logs.
//
// Filter is benefitId OR paymentBotTaskId: some PPS log lines carry only the
// task id (no benefitId), so an OR (`?` prefix) catches both. Plain quoted
// terms — the format confirmed working against the live console.
//
// HIPAA: no PHI in the URL. `benefitId` is an opaque internal UUID (not
// member-identifying on its own); PPS/EPS logs are PHI-scrubbed (lastFour only,
// no raw error messages). Region / log-group / internal IDs only.

import {
  isLocalDevWithBackend,
  isProduction,
} from '../../../../../../../modules/app-config/environment'

// PPS runs in us-west-2 only (single region).
export const CLOUDWATCH_REGION = 'us-west-2'

// Lambda default log-group naming: /aws/lambda/<service>-<stage>-<function>.
// Service = partner-payment-service, function = api (serverless.yml). PPS has no
// local Lambda, so local dev points at the dev log group (mirrors getApiUrl()'s
// LOCAL -> dev-backend behaviour).
const PPS_LOG_GROUP = {
  DEV: '/aws/lambda/partner-payment-service-dev-api',
  PROD: '/aws/lambda/partner-payment-service-prod-api',
  LOCAL: '/aws/lambda/partner-payment-service-dev-api',
}

// Resolves the log group at runtime from the hostname-derived environment — the
// same pattern as getApiUrl() in modules/enrollment-platform-api. A prod build
// served on a dev host links to dev logs, not prod.
export const getPpsLogGroup = (): string => {
  if (isProduction()) {
    return PPS_LOG_GROUP.PROD
  }
  if (isLocalDevWithBackend()) {
    return PPS_LOG_GROUP.LOCAL
  }
  return PPS_LOG_GROUP.DEV
}

// Window padding around the failure timestamp (ms). The precise window is the
// task's createdAt → updatedAt, but the dashboard DTO only exposes `failedAt`
// today, so we pad. `before` is wider because a bot can run for a while before
// it fails. CloudWatch scopes to the right date-partitioned streams from this.
const WINDOW_BEFORE_MS = 60 * 60 * 1000
const WINDOW_AFTER_MS = 15 * 60 * 1000

interface PpsLogsLinkArgs {
  benefitId: string | null
  paymentBotTaskId: string
  /** ISO timestamp of the failure (RecentFailureDto.failedAt) */
  failedAtIso: string
}

/**
 * CloudWatch console fragment encoding. The console double-encodes and writes
 * `%` as `$`: URL-encode the (already once-encoded) value, then swap `%`→`$`.
 *   "/"  -> %2F  -> $252F
 *   '"'  -> %22  -> $2522
 *   "?"  -> %3F  -> $3F   (used literally for the separators below)
 */
function cw(once: string): string {
  return encodeURIComponent(once).replace(/%/g, '$')
}

export function buildPpsLogsUrl({
  benefitId,
  paymentBotTaskId,
  failedAtIso,
}: PpsLogsLinkArgs): string {
  const parsedMs = new Date(failedAtIso).getTime()
  const failedAtMs = Number.isNaN(parsedMs) ? Date.now() : parsedMs
  const start = failedAtMs - WINDOW_BEFORE_MS
  const end = failedAtMs + WINDOW_AFTER_MS

  // OR filter — match lines containing EITHER identifier. `?` prefix = OR.
  const terms: string[] = []
  if (benefitId) terms.push(`?"${benefitId}"`)
  terms.push(`?"${paymentBotTaskId}"`)
  const filterPattern = terms.join(' ')

  // Log group is double-encoded (value-encode, then console-encode).
  const group = cw(encodeURIComponent(getPpsLogGroup()))
  const fp = cw(encodeURIComponent(filterPattern))

  // Separators written in the console's $-scheme: $3F=? $26=& $3D==
  const query = `start$3D${start}$26end$3D${end}$26filterPattern$3D${fp}`

  return (
    `https://${CLOUDWATCH_REGION}.console.aws.amazon.com/cloudwatch/home` +
    `?region=${CLOUDWATCH_REGION}` +
    `#logsV2:log-groups/log-group/${group}/log-events$3F${query}`
  )
}
